import { Box } from '@mui/material'
import LamenessList from 'components/cattles/lamenessPage/ui/LamenessList'
import { useMultiSelect } from 'hook/useMultiSelect'
import { useTabsController } from 'hooks/useTabsController'
import PageContainer from 'layouts/PageContainer'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { fetchLightGroupList } from 'store/groupsSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { setLamenessFilters } from 'store/lamenessFilterSlice'
import { COLORS } from 'styles/colors'
import BreadCrumbsGroup from 'ui/breadCrumbs/BreadCrumbsGroup'
import TabsFilter from 'ui/breadCrumbs/TabsFilter'
import LamenessChart from './ui/LamenessCardChart'
import LamenessInfo from './ui/LamenessInfo'
import LamenessTrim from './ui/LamenessTrim'
import TreatmentCard from './ui/TreatmentCard'

const parseGroupIds = (query: string | null) => {
	if (!query) return []

	return query
		.split(',')
		.map(Number)
		.filter(id => Number.isFinite(id) && id > 0)
}

const sameGroupIds = (left: number[], right: number[]) =>
	left.length === right.length && left.every(id => right.includes(id))

enum LAMENESS_TABS {
	LAMENESS = 'lameness',
	TRIM = 'trim',
	TREATMENT = 'treatment',
}

const LamenessTabContent = () => {
	const dispatch = useAppDispatch()
	const [searchParams] = useSearchParams()
	const { lightGroupList } = useAppSelector(state => state.groupsReducer)
	const groupFromQuery = searchParams.get('group')
	const initialGroups = useMemo(
		() => parseGroupIds(groupFromQuery),
		[groupFromQuery],
	)
	const groupFilters = useMultiSelect<number>(initialGroups)

	useEffect(() => {
		if (!lightGroupList) {
			dispatch(fetchLightGroupList())
		}
	}, [dispatch, lightGroupList])

	useEffect(() => {
		groupFilters.setSelected(parseGroupIds(groupFromQuery))
	}, [groupFromQuery, groupFilters.setSelected])

	const groupQuery = groupFilters.selected.length
		? groupFilters.selected.join(',')
		: undefined

	const groupChips = useMemo(
		() =>
			(lightGroupList ?? []).map(item => ({
				id: item.id,
				title: item.title,
				selected: groupFilters.selected.includes(item.id),
			})),
		[lightGroupList, groupFilters.selected],
	)

	return (
		<>
			<BreadCrumbsGroup
				handleActiveFilter={id => {
					if (id === 'all' || typeof id === 'number') {
						groupFilters.toggle(id)
					}
				}}
				handleRemoveFilter={id => {
					if (typeof id === 'number') {
						groupFilters.remove(id)
					}
				}}
				array={groupChips}
			/>
			<LamenessInfo groupIds={groupQuery} />
			<LamenessChart groupIds={groupQuery} />
			<LamenessList pageGroupIds={groupFilters.selected} />
		</>
	)
}

const renderContent = (currentTab: LAMENESS_TABS) => {
	switch (currentTab) {
		case LAMENESS_TABS.LAMENESS:
			return <LamenessTabContent />

		case LAMENESS_TABS.TRIM:
			return <LamenessTrim />

		case LAMENESS_TABS.TREATMENT:
			return <TreatmentCard />

		default:
			return null
	}
}

const LamenessPage = () => {
	const dispatch = useAppDispatch()
	const [searchParams] = useSearchParams()
	const lamenessFilters = useAppSelector(
		state => state.lamenessFilterReducer.lamenessFilters,
	)
	const groupFromQuery = searchParams.get('group')
	const queryGroupIds = parseGroupIds(groupFromQuery)
	const lamenessFiltersRef = useRef(lamenessFilters)
	lamenessFiltersRef.current = lamenessFilters
	const [groupFilterReady, setGroupFilterReady] = useState(() =>
		sameGroupIds(queryGroupIds, lamenessFilters.groups.ids),
	)

	useLayoutEffect(() => {
		const nextGroupIds = parseGroupIds(groupFromQuery)
		const currentFilters = lamenessFiltersRef.current
		if (!sameGroupIds(nextGroupIds, currentFilters.groups.ids)) {
			dispatch(
				setLamenessFilters({
					...currentFilters,
					groups: { ids: nextGroupIds },
				}),
			)
		}
		setGroupFilterReady(true)
	}, [dispatch, groupFromQuery])

	const { tab, changeTab } = useTabsController<LAMENESS_TABS>({
		keyName: 'tab',
		defaultValue: LAMENESS_TABS.LAMENESS,
	})

	const { permissions } = useAppSelector(state => state.authReducer)

	const tabOptions = [
		{
			key: LAMENESS_TABS.LAMENESS,
			title: 'Хромота',
		},
		...(permissions?.license_limpness
			? [
					{
						key: LAMENESS_TABS.TRIM,
						title: 'Обрезка копыт',
					},
				]
			: []),
		...(permissions?.license_limpness
			? [
					{
						key: LAMENESS_TABS.TREATMENT,
						title: 'Схемы лечения',
					},
				]
			: []),
	]

	if (!tabOptions.find(t => t.key === tab)) {
		changeTab(tabOptions[0].key)
	}

	return (
		<PageContainer>
			<Box
				sx={{
					position: 'sticky',
					top: 0,
					backgroundColor: COLORS.background,
					zIndex: 90,
				}}
			>
				<TabsFilter
					availableTabs={tabOptions}
					selectedKey={tab}
					onClick={key => changeTab(key as LAMENESS_TABS)}
				/>
			</Box>

			{groupFilterReady ? renderContent(tab) : null}
		</PageContainer>
	)
}

export default LamenessPage
